import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  AlertTitle,
  Autocomplete,
  TextField,
  Button,
  Grid,
  Paper,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import api from '../services/api.service';

const ReceiveEquipmentStep = ({
  formData,
  setFormData,
  isAutoFilled,
  showTemplateSearch,
  selectedTemplate,
  setSelectedTemplate,
  onTemplateCreate,
  onTemplateSelect
}) => {
  const [templateSearchText, setTemplateSearchText] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    manufacturer: '',
    model: '',
    description: '',
    barcodePattern: '',
    notes: ''
  });

  // Auto-populate search when barcode not found
  useEffect(() => {
    if (showTemplateSearch && !isAutoFilled && formData.barcode && !templateSearchText) {
      setTemplateSearchText(formData.barcode);
    }
  }, [showTemplateSearch, isAutoFilled, formData.barcode]);

  // Load templates when search text changes
  useEffect(() => {
    if (templateSearchText.length >= 2) {
      loadTemplates(templateSearchText);
    } else if (templateSearchText.length === 0) {
      setAvailableTemplates([]);
    }
  }, [templateSearchText]);

  const loadTemplates = async (searchText = '') => {
    if (!searchText && templateSearchText.length < 2) return;
    
    setLoadingTemplates(true);
    try {
      const response = await api.get(`/equipment-templates`, {
        params: { search: searchText || templateSearchText, limit: 20 }
      });
      
      const templates = response.data?.templates || response.data || [];
      setAvailableTemplates(templates.map(template => ({
        ...template,
        label: `${template.template_data?.manufacturer || ''} ${template.template_data?.model || ''} - ${template.template_data?.description || ''}`.trim(),
        value: template.id
      })));
    } catch (error) {
      console.error('Error loading templates:', error);
      setAvailableTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = async (template) => {
    if (!template) return;
    
    setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      manufacturer: template.template_data?.manufacturer || template.manufacturer || '',
      model: template.template_data?.model || template.model || '',
      description: template.template_data?.description || template.description || ''
    }));
    setShowTemplateForm(false);
    
    // Trigger smart assignment if callback is provided
    if (onTemplateSelect) {
      await onTemplateSelect(template);
    }
  };

  const handleCreateNewTemplate = async () => {
    if (!templateFormData.manufacturer || !templateFormData.model || !templateFormData.description) {
      return;
    }

    try {
      const templateData = {
        name: templateFormData.name || `${templateFormData.manufacturer} ${templateFormData.model}`,
        manufacturer: templateFormData.manufacturer,
        model: templateFormData.model,
        description: templateFormData.description,
        barcode_pattern: templateFormData.barcodePattern, // Use template barcode field
        notes: templateFormData.notes
      };

      let response, updatedTemplate;
      
      if (selectedTemplate && selectedTemplate.id) {
        // Update existing template
        response = await api.put(`/equipment-templates/${selectedTemplate.id}`, templateData);
        updatedTemplate = response.data?.data || response.data;
      } else {
        // Create new template
        response = await api.post('/equipment-templates', templateData);
        updatedTemplate = response.data?.data || response.data;
      }
      
      // Create a properly formatted template object
      const formattedTemplate = {
        ...updatedTemplate,
        id: updatedTemplate?.id || selectedTemplate?.id,
        manufacturer: templateFormData.manufacturer,
        model: templateFormData.model,
        description: templateFormData.description,
        template_data: {
          manufacturer: templateFormData.manufacturer,
          model: templateFormData.model,
          description: templateFormData.description
        }
      };
      
      // Set the template as selected
      handleTemplateSelect(formattedTemplate);
      
      // Clear template form
      setTemplateFormData({
        name: '',
        manufacturer: '',
        model: '',
        description: '',
        barcodePattern: '',
        notes: ''
      });
      
      setShowTemplateForm(false);

      // Reload templates
      await loadTemplates(templateSearchText);
      
      if (onTemplateCreate) {
        onTemplateCreate(updatedTemplate);
      }
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };


  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.primary' }}>
        Step 2: Equipment Selection
        {isAutoFilled && (
          <Chip 
            label="Auto-filled from Barcode" 
            color="success" 
            size="small"
            sx={{ ml: 2, verticalAlign: 'middle' }}
          />
        )}
        {showTemplateSearch && !isAutoFilled && (
          <Chip 
            label="Barcode Not Found" 
            color="warning" 
            size="small"
            sx={{ ml: 2, verticalAlign: 'middle' }}
          />
        )}
      </Typography>
      
      
      {/* Template Search when barcode not found AND no template selected */}
      {(showTemplateSearch && !isAutoFilled && !selectedTemplate) && (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Barcode Not Found</AlertTitle>
            <Typography variant="body2">
              Barcode <strong>{formData.barcode}</strong> was not recognized in the system.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please search for an equipment template below or create a new one.
            </Typography>
          </Alert>

          {/* Show create template button when barcode not found */}
          {!selectedTemplate && !showTemplateForm && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No equipment template found for barcode "{formData.barcode}"
              </Typography>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={() => {
                  setShowTemplateForm(true);
                  // Pre-fill barcode with the scanned barcode
                  setTemplateFormData(prev => ({
                    ...prev,
                    barcodePattern: formData.barcode || ''
                  }));
                }}
                sx={{ mb: 2 }}
              >
                + Create Template for "{formData.barcode}"
              </Button>
            </Box>
          )}

        </Box>
      )}

      {/* Inline Template Creation Form - moved outside conditional */}
      {showTemplateForm && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {selectedTemplate && selectedTemplate.id ? 'Edit Equipment Template:' : 'Create New Equipment Template:'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Barcode"
                fullWidth
                value={templateFormData.barcodePattern}
                onChange={(e) => setTemplateFormData(prev => ({ ...prev, barcodePattern: e.target.value }))}
                placeholder="Enter barcode for this template (optional)"
                helperText="Barcode that identifies this equipment type"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Manufacturer *"
                fullWidth
                required
                value={templateFormData.manufacturer}
                onChange={(e) => setTemplateFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
                placeholder="e.g., Canon, Shure, ETC"
                error={!templateFormData.manufacturer}
                helperText={!templateFormData.manufacturer ? "Manufacturer is required" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Model *"
                fullWidth
                required
                value={templateFormData.model}
                onChange={(e) => setTemplateFormData(prev => ({ ...prev, model: e.target.value }))}
                placeholder="e.g., EOS R5, SM58, Source Four"
                error={!templateFormData.model}
                helperText={!templateFormData.model ? "Model is required" : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description *"
                fullWidth
                required
                value={templateFormData.description}
                onChange={(e) => setTemplateFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the equipment"
                error={!templateFormData.description}
                helperText={!templateFormData.description ? "Description is required" : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={templateFormData.notes}
                onChange={(e) => setTemplateFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes about this equipment type..."
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNewTemplate}
                  disabled={!templateFormData.manufacturer || !templateFormData.model || !templateFormData.description}
                >
                  {selectedTemplate && selectedTemplate.id ? 'Update Template' : 'Create Template'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowTemplateForm(false);
                    setTemplateFormData({
                      name: '',
                      manufacturer: '',
                      model: '',
                      description: '',
                      barcodePattern: '',
                      notes: ''
                    });
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Show auto-filled equipment fields when template is matched OR selected */}
      {(isAutoFilled || selectedTemplate) && !showTemplateForm && (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Manufacturer"
                fullWidth
                value={formData.manufacturer || ''}
                InputProps={{
                  readOnly: true,
                }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Model"
                fullWidth
                value={formData.model || ''}
                InputProps={{
                  readOnly: true,
                }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Description"
                fullWidth
                value={formData.description || ''}
                InputProps={{
                  readOnly: true,
                }}
                variant="filled"
              />
            </Grid>
          </Grid>

          {/* Show template edit/remove buttons when template is selected */}
          {selectedTemplate && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => {
                  console.log('Edit template clicked', selectedTemplate);
                  // Pre-populate form with existing template data
                  setTemplateFormData({
                    name: selectedTemplate.name || '',
                    manufacturer: selectedTemplate.template_data?.manufacturer || selectedTemplate.manufacturer || '',
                    model: selectedTemplate.template_data?.model || selectedTemplate.model || '',
                    description: selectedTemplate.template_data?.description || selectedTemplate.description || '',
                    barcodePattern: selectedTemplate.barcode_pattern || '',
                    notes: selectedTemplate.notes || ''
                  });
                  setShowTemplateForm(true);
                  console.log('showTemplateForm set to true');
                }}
              >
                Edit Template
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => {
                  setSelectedTemplate(null);
                  setFormData(prev => ({
                    ...prev,
                    manufacturer: '',
                    model: '',
                    description: ''
                  }));
                }}
              >
                Remove Template
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReceiveEquipmentStep;